import React, { useState } from 'react';
import { Download, CheckCircle } from 'lucide-react';
import InfoBox from './components/InfoBox';
import UploadDropzone from './components/UploadDropzone';
import StatsCards from './components/StatsCards';
import ValidationErrors from './components/ValidationErrors';
import DataPreview from './components/DataPreview';
import './SuperAdminBulkUploader.css';

interface ValidationError {
  row: number;
  field: string;
  error: string;
}

interface PreviewData {
  [key: string]: string;
}

type TabType = 'users' | 'content' | 'assignments';

interface TabConfig {
  title: string;
  subtitle: string;
  requirements: {
    required: string;
    optional: string;
    additional?: string;
  };
  previewColumns: string[];
}

interface UploadStats {
  totalRows: number;
  valid: number;
  errors: number;
  warnings: number;
  duplicates: number;
}

const BulkUploadPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('users');
  const [file, setFile] = useState<File | null>(null);
  const [stats, setStats] = useState<UploadStats | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [previewData, setPreviewData] = useState<PreviewData[]>([]);

  const tabConfigs: Record<TabType, TabConfig> = {
    users: {
      title: 'Bulk Upload Users',
      subtitle: 'Upload multiple users using CSV file',
      requirements: {
        required: 'First Name, Last Name, Email, Department, Job Title',
        optional: 'Manager Email, Phone, Location, Hire Date',
        additional: 'Maximum 1000 rows per upload'
      },
      previewColumns: ['NAME', 'EMAIL', 'DEPARTMENT', 'JOB TITLE']
    },
    content: {
      title: 'Bulk Update Content',
      subtitle: 'Update content metadata in bulk using CSV',
      requirements: {
        required: 'Content ID, Field to Update, New Value',
        optional: 'Title, Description, Category, Tags, Status, Owner',
        additional: 'Changes will overwrite existing values'
      },
      previewColumns: ['CONTENT ID', 'FIELD', 'NEW VALUE', 'CATEGORY']
    },
    assignments: {
      title: 'Bulk Upload Assignments / Training',
      subtitle: 'Assign courses to multiple users via CSV',
      requirements: {
        required: 'User Email, Course ID or Name',
        optional: 'Due Date, Send Notification (Yes/No)',
        additional: 'Users must exist in the system'
      },
      previewColumns: ['USER EMAIL', 'COURSE', 'DUE DATE', 'NOTIFICATION']
    }
  };

  const currentConfig = tabConfigs[activeTab];

  const handleFileSelect = (selectedFile: File) => {
    setFile(selectedFile);
    
    // Simulate file processing based on active tab
    setStats({
      totalRows: 150,
      valid: 145,
      errors: 2,
      warnings: 3,
      duplicates: 0
    });

    // Generate sample data based on active tab
    if (activeTab === 'users') {
      setValidationErrors([
        { row: 15, field: 'email', error: 'Invalid email format' },
        { row: 23, field: 'department', error: 'Department not found' }
      ]);

      setPreviewData([
        { name: 'John Doe 1', email: 'john.doe1@company.com', department: 'Engineering', jobTitle: 'Software Engineer' },
        { name: 'John Doe 2', email: 'john.doe2@company.com', department: 'Engineering', jobTitle: 'Software Engineer' },
        { name: 'John Doe 3', email: 'john.doe3@company.com', department: 'Engineering', jobTitle: 'Software Engineer' },
        { name: 'John Doe 4', email: 'john.doe4@company.com', department: 'Engineering', jobTitle: 'Software Engineer' },
        { name: 'John Doe 5', email: 'john.doe5@company.com', department: 'Engineering', jobTitle: 'Software Engineer' }
      ]);
    } else if (activeTab === 'content') {
      setValidationErrors([
        { row: 8, field: 'contentId', error: 'Content ID does not exist' },
        { row: 12, field: 'newValue', error: 'Invalid value format' }
      ]);

      setPreviewData([
        { contentId: 'CNT-001', field: 'Title', newValue: 'Updated Course Title', category: 'Technical' },
        { contentId: 'CNT-002', field: 'Description', newValue: 'New course description', category: 'Compliance' },
        { contentId: 'CNT-003', field: 'Status', newValue: 'Active', category: 'Leadership' },
        { contentId: 'CNT-004', field: 'Owner', newValue: 'jane.smith@company.com', category: 'Technical' },
        { contentId: 'CNT-005', field: 'Tags', newValue: 'python,coding,beginner', category: 'Technical' }
      ]);
    } else {
      setValidationErrors([
        { row: 5, field: 'userEmail', error: 'User does not exist in system' },
        { row: 18, field: 'courseId', error: 'Invalid course ID' }
      ]);

      setPreviewData([
        { userEmail: 'john.doe1@company.com', course: 'Security Fundamentals', dueDate: '2025-01-15', notification: 'Yes' },
        { userEmail: 'john.doe2@company.com', course: 'Data Privacy 101', dueDate: '2025-01-20', notification: 'Yes' },
        { userEmail: 'john.doe3@company.com', course: 'Leadership Training', dueDate: '2025-02-01', notification: 'No' },
        { userEmail: 'john.doe4@company.com', course: 'Project Management', dueDate: '2025-02-15', notification: 'Yes' },
        { userEmail: 'john.doe5@company.com', course: 'Communication Skills', dueDate: '2025-03-01', notification: 'Yes' }
      ]);
    }
  };

  const handleCancel = () => {
    setFile(null);
    setStats(null);
    setValidationErrors([]);
    setPreviewData([]);
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    // Reset state when changing tabs
    handleCancel();
  };

  const handleConfirmUpload = () => {
    alert('Uploading users...');
  };

  return (
    <div className="s-admin-bulk-upload-container">
      <div className="s-admin-bulk-upload-breadcrumb">
        <span className="s-admin-bulk-upload-breadcrumb-item">Tools</span>
        <span className="s-admin-bulk-upload-breadcrumb-separator">&gt;</span>
        <span className="s-admin-bulk-upload-breadcrumb-item">Bulk Uploader</span>
      </div>

      <h1 className="s-admin-bulk-upload-title">Tools</h1>
      <p className="s-admin-bulk-upload-subtitle">Administrative utilities and system tools</p>

      <div className="s-admin-bulk-upload-tabs">
        <button 
          className={`s-admin-bulk-upload-tab ${activeTab === 'users' ? 's-admin-bulk-upload-tab-active' : ''}`}
          onClick={() => handleTabChange('users')}
        >
          Upload Users
        </button>
        <button 
          className={`s-admin-bulk-upload-tab ${activeTab === 'content' ? 's-admin-bulk-upload-tab-active' : ''}`}
          onClick={() => handleTabChange('content')}
        >
          Upload Content
        </button>
        <button 
          className={`s-admin-bulk-upload-tab ${activeTab === 'assignments' ? 's-admin-bulk-upload-tab-active' : ''}`}
          onClick={() => handleTabChange('assignments')}
        >
          Upload Assignments / Training
        </button>
      </div>

      <div className="s-admin-bulk-upload-header">
        <div>
          <h2 className="s-admin-bulk-upload-section-title">{currentConfig.title}</h2>
          <p className="s-admin-bulk-upload-section-subtitle">{currentConfig.subtitle}</p>
        </div>
        <button className="s-admin-bulk-upload-download-btn">
          <Download size={16} />
          Download Template
        </button>
      </div>

      <InfoBox requirements={currentConfig.requirements} />

      {!file ? (
        <UploadDropzone onFileSelect={handleFileSelect} />
      ) : (
        <>
          {stats && <StatsCards stats={stats} />}

          <ValidationErrors errors={validationErrors} />

          <DataPreview data={previewData} columns={currentConfig.previewColumns} />

          <div className="s-admin-bulk-upload-actions">
            <button className="s-admin-bulk-upload-cancel-btn" onClick={handleCancel}>
              Cancel
            </button>
            <button className="s-admin-bulk-upload-confirm-btn" onClick={handleConfirmUpload}>
              <CheckCircle size={16} />
              Confirm & Upload
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default BulkUploadPage;